import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAnimationTuning } from '@/app/playbook/animation/animationTuningContext'
import {
  CAPABILITIES_FUTURE_LINE_DASH_GAP,
  CHINA_LINE_RISE_DURATION_MS,
  GRAPH_LABEL_FADE_DURATION_MS,
  PLAN_B_CHINA_CAPABILITY_LINE_COLOR,
  PLAN_KEYS,
  defaultGraphData,
} from './graphConstants'
import { useBackgroundPatternSettings } from './BackgroundPatternSettingsProvider'
import { densityFromConfidenceStops } from './backgroundPatternStore'
import {
  ExtrapolatedGraphLossOfControlLayer,
  getLossOfControlAccentColor,
} from './ExtrapolatedGraphLossOfControlLayer'
import { currentLevelRiskAt } from './takeoverRiskLikelihoodData'
import {
  createRedAboveLossOfControlDitherCanvasLayer,
  createRedAboveLossOfControlPathRender,
  createRawProgressAtPointLookup,
  type FadingCapabilityLinePolylines,
  type TileShadeField,
} from './RedAboveLossOfControlOverlay'
import {
  TRAILING_ACTOR_RISK_YEARS,
  createTrailingActorRiskConfidenceEvaluator,
  survivalYearsForDate,
  survivalYearsForYear,
  type TrailingActorRiskCapParams,
  type TrailingActorRiskYearParams,
} from './trailingActorRiskBand'
import { getIsSafariBrowser } from './browserUtils'
import { LossOfControlEditorDots } from './LossOfControlEditorDots'
import { useLossOfControlEditor } from './LossOfControlEditorProvider'
import { createAICapabilitiesLabelAnnotation } from './AICapabilitiesLabel'
import {
  useAnimatedTimestamp,
  useTimedGraphViewport,
  usePlanBChinaLineRiseProgress,
  useTimedAnimatedGraphPoints,
  easeInOutCubic,
  easeTimedGraphPointProgress,
} from './hooks'
import {
  appendProjectedLossOfControlRenderSourceState,
  createCapabilityThresholdOverlay,
  createCurrentPoint,
  createDefaultRenderSourceState,
  createEvenlySpacedTimestampPositions,
  createRenderedMarkers,
  createSmoothedScreenPoints,
  createValueForY,
  createXForTimestamp,
  createYForValue,
  formatSpeedupValue,
  formatSpeedupValueWithTwoSignificantDigits,
  getPrimaryVisibleGraphPoints,
  getTimelineBounds,
  getVisibleGraphState,
  getYDomainMax,
  resolveScaleSettings,
  toGraphPoints,
} from './graphUtils'
import {
  addGalaxyTextShadow,
  createBottomAxisLabels,
  createCurrentValueLabel,
  createCurrentValueLabelPosition,
  createFutureValueAnnotation,
  createSideUnitAnnotations,
} from './graphAnnotations'
import {
  createHoverPoints,
  createLossOfControlHoverOpacitySource,
  createRenderedGraphLineSegments,
  createRiskBackgroundLabels,
  getVisibleDataPointTimestampsForAxis,
} from './extrapolatedGraphRenderUtils'
import { useTransitioningAxisLabels } from './useTransitioningAxisLabels'
import type {
  ExtrapolatedGraphAnnotation,
  ExtrapolatedGraphProps,
  ExtrapolatedGraphRenderProps,
  GraphLineSource,
  GraphMarkerSource,
  GraphPoint,
  GraphRenderSourceState,
} from './types'

const clampChinaRise01 = (value: number) => Math.min(1, Math.max(0, value))

const applyChinaLineRiseToGraphPoints = (
  graphPoints: GraphPoint[],
  riseGlobalLinear: number,
): GraphPoint[] => {
  if (graphPoints.length === 0) return graphPoints
  const orderedTimelinePoints = [...graphPoints].sort((pointA, pointB) =>
    pointA.timestamp - pointB.timestamp
  )
  const pointIndexByTimestamp = new Map(
    orderedTimelinePoints.map((point, pointIndex) => [
      point.timestamp,
      pointIndex,
    ])
  )
  const pointCount = orderedTimelinePoints.length
  return graphPoints.map(graphPoint => {
    const pointIndex = pointIndexByTimestamp.get(graphPoint.timestamp) ?? 0
    const segmentWidth = pointCount <= 1 ? 1 : 1 / pointCount
    const segmentStart = pointCount <= 1 ? 0 : pointIndex * segmentWidth
    const localT = pointCount <= 1
      ? clampChinaRise01(riseGlobalLinear)
      : clampChinaRise01((riseGlobalLinear - segmentStart) / segmentWidth)
    const eased = easeInOutCubic(localT)
    return {
      ...graphPoint,
      value: graphPoint.value * eased,
    }
  })
}

const GHOST_INJECTABLE_LINE_SOURCE_KEYS = new Set([
  'future',
  'projected-loss-of-control',
  'projected-loss-of-control-triple',
  'plan-b-china-future',
])
// Plan-preview lines (see ExtrapolatedGraphProps.planPreviewLines): black at
// the thinnest width that still reads as a black line — sub-1px strokes
// antialias toward grey, which the treatment explicitly avoids.
const PLAN_PREVIEW_LINE_COLOR = '#000000'
const PLAN_PREVIEW_LINE_STROKE_WIDTH_PX = 1
// A fan line's fully-dotted dash (see planPreviewDotProgressByLineKey): a
// px longer than the stroke width — with the canvas's butt caps each dash
// reads as a small 2×1px mark; a bare stroke-width square was too faint.
const PLAN_PREVIEW_DOT_DASH_LENGTH_PX = PLAN_PREVIEW_LINE_STROKE_WIDTH_PX + 1
// Keeps a fan line's end label (planPreviewEndLabelByLineKey) inside the plot
// when the line has exited the top of the frame (e.g. the default plan
// against the pre-choice 3.0 ceiling): the label's anchor sits at least its
// own line box (~20px at the 13px label font) below the plot top, pinning it
// at the edge the line vanished through.
const PLAN_PREVIEW_END_LABEL_MIN_Y_PX = 20
// Gap between a fan line's end label and the line itself, applied on both
// axes (label sits above and to the left of the stroke) so the text never
// overlaps the line whether it exits the frame steeply or flat.
const PLAN_PREVIEW_END_LABEL_LINE_CLEARANCE_PX = 10
// Slightly larger than the stock 10px annotation text so the plan name reads
// as the fan's headline.
const PLAN_PREVIEW_END_LABEL_FONT_SIZE_PX = 13
// Minimum height of the label's flat box (the 13px text's ~1.5 line box);
// the rendered box rounds this up to whole dither rows.
const PLAN_PREVIEW_END_LABEL_MIN_BOX_HEIGHT_PX = 20
const LINE_RENDER_MIN_OVERFLOW_PX = 48
const LINE_RENDER_VERTICAL_OVERFLOW_RATIO = 0.25

const areNumberArraysEqual = (first: number[], second: number[]) =>
  first.length === second.length &&
  first.every((value, index) => value === second[index])

// X where a screen-space polyline crosses the given y, taking the crossing
// nearest the polyline's end; undefined when it never reaches that height.
const findLineXAtY = (
  points: { x: number; y: number }[],
  targetY: number,
): number | undefined => {
  for (let i = points.length - 1; i >= 1; i--) {
    const from = points[i - 1]
    const to = points[i]
    if ((from.y - targetY) * (to.y - targetY) <= 0 && from.y !== to.y) {
      return from.x + ((targetY - from.y) / (to.y - from.y)) * (to.x - from.x)
    }
  }
  return undefined
}

const useStableNumberArray = (values: number[]) => {
  const stableValuesRef = useRef(values)
  if (!areNumberArraysEqual(stableValuesRef.current, values)) {
    stableValuesRef.current = values
  }
  return stableValuesRef.current
}

const buildGhostInjectedRenderSourceState = ({
  baseRenderSourceState,
  ghostTimestamps,
  ghostDataSourcesByLineKey,
}: {
  baseRenderSourceState: GraphRenderSourceState
  ghostTimestamps: number[]
  ghostDataSourcesByLineKey: Record<string, GraphPoint[]>
}): GraphRenderSourceState => {
  if (ghostTimestamps.length === 0) return baseRenderSourceState
  const collectGhostPoints = (sourceKey: string): GraphPoint[] => {
    const sourcePoints = ghostDataSourcesByLineKey[sourceKey]
    if (!sourcePoints) return []
    const sortedSourcePoints = [...sourcePoints].sort((pointA, pointB) =>
      pointA.timestamp - pointB.timestamp
    )
    return ghostTimestamps.flatMap(ghostTimestamp => {
      const matchingPoint = sortedSourcePoints.find(
        point => point.timestamp === ghostTimestamp,
      )
      if (matchingPoint) return [matchingPoint]
      const firstSourcePoint = sortedSourcePoints[0]
      const lastSourcePoint = sortedSourcePoints[sortedSourcePoints.length - 1]
      if (
        !firstSourcePoint ||
        !lastSourcePoint ||
        ghostTimestamp < firstSourcePoint.timestamp ||
        ghostTimestamp > lastSourcePoint.timestamp
      ) return []
      return [createCurrentPoint(ghostTimestamp, sortedSourcePoints)]
    })
  }
  const augmentLineSource = (lineSource: GraphLineSource): GraphLineSource => {
    if (!GHOST_INJECTABLE_LINE_SOURCE_KEYS.has(lineSource.key)) return lineSource
    const ghostPoints = collectGhostPoints(lineSource.key)
    if (ghostPoints.length === 0) return lineSource
    return {
      ...lineSource,
      graphPoints: [...lineSource.graphPoints, ...ghostPoints]
        .sort((pointA, pointB) => pointA.timestamp - pointB.timestamp),
    }
  }
  const augmentMarkerSource = (markerSource: GraphMarkerSource): GraphMarkerSource => {
    if (!GHOST_INJECTABLE_LINE_SOURCE_KEYS.has(markerSource.key)) return markerSource
    const ghostPoints = collectGhostPoints(markerSource.key)
    if (ghostPoints.length === 0) return markerSource
    return {
      ...markerSource,
      graphPoints: [...markerSource.graphPoints, ...ghostPoints]
        .sort((pointA, pointB) => pointA.timestamp - pointB.timestamp),
    }
  }
  return {
    ...baseRenderSourceState,
    lineSources: baseRenderSourceState.lineSources.map(augmentLineSource),
    futureMarkerSources:
      baseRenderSourceState.futureMarkerSources.map(augmentMarkerSource),
  }
}


// The trailing-actor-risk field's data starts with the capability line
// (2025-01), which makes its left edge blend into the line's tail at the
// timeline start. Start the field half a year later instead.
const TRAILING_ACTOR_RISK_START_TIMESTAMP = Date.UTC(2025, 6, 1) // 2025-07-01

// Drops a series' points before startTimestamp and interpolates a point
// exactly there, so a band's left edge lands on the date rather than
// snapping to the next datum.
const clipGraphPointsBefore = (points: GraphPoint[], startTimestamp: number): GraphPoint[] => {
  if (points.length === 0 || points[0].timestamp >= startTimestamp) return points
  const after = points.filter(p => p.timestamp > startTimestamp)
  if (after.length === 0) return []
  const before = points.filter(p => p.timestamp <= startTimestamp)
  const prev = before[before.length - 1]
  const next = after[0]
  const value = prev.value
    + (next.value - prev.value) * (startTimestamp - prev.timestamp) / (next.timestamp - prev.timestamp)
  return [
    { date: new Date(startTimestamp).toISOString().slice(0, 10), timestamp: startTimestamp, value },
    ...after,
  ]
}

export const useExtrapolatedGraphRenderProps = ({
  currentDate,
  targetDate,
  width,
  height,
  textColor = 'currentColor',
  backgroundColor = '#fffff8',
  title,
  activePlan = 'D',
  trailingActorRiskYearShift = 0,
  showGalaxy = false,
  showLossOfControlLines = true,
  showAICapabilitiesLabel = true,
  lossOfControlHoverReveal = false,
  redAboveLossOfControl = false,
  redAboveLossOfControlFaded = false,
  blackBelowTooSlowRisk = false,
  blackPatternSettings,
  showRiskBackgroundLabels = false,
  showCapabilityThresholdLabels = true,
  numCapabilityThresholdLabels = 0,
  showSideUnits = false,
  showTimelineUnits = true,
  showAllYears = false,
  showYearDots = true,
  showCurrentPointMarker = false,
  hideCapabilityFutureLine = false,
  hideCapabilityLine = false,
  showTopDashedLine = false,
  useLogScale = false,
  scaleSettings,
  graphDataFile = defaultGraphData,
  planPreviewLines,
  planPreviewLinesOpacity = 1,
  planPreviewSplitFrozen = false,
  planPreviewDeemphasizedLinesOpacity = 1,
  planPreviewEmphasizedLineKey = null,
  planPreviewDotProgressByLineKey,
  planPreviewEndLabelByLineKey,
  capabilityLineMorphDisabled = false,
  showPlanBChinaCapabilityLine = false,
  graphLabel = '',
  formatCurrentValue = formatSpeedupValue,
  formatProjectedValue = formatSpeedupValueWithTwoSignificantDigits,
  safeZoneColor,
  safeZoneColorLeft,
  safeZoneOpacity = 1,
  safeZoneGradientMidpoint = 0.5,
  safeZoneTopOffsetPx = 0,
  safeZoneBottomOffsetPx = 0,
  safeZoneEdgeBlurPx = 0,
  currentYearPill = false,
  currentYearPillShowMonth = false,
  currentYearPillFlushLeft = false,
  onCurrentYearPillClick,
  currentYearPillMenuOpen,
  capabilityPastLineColor,
  capabilityDashedLineColor,
  capabilityPastLineStrokeWidth,
  clipLinesToDitherTop,
  capabilityLineFloorValue,
  currentPointMarkerRadius,
  showCurrentPointCapabilityLabel = false,
  bottomAxisYears,
  customAnnotations,
  onHoveredYearChange,
  pointTooltipDetailsHint,
  pointHoverMaxDistanceToLinePx,
  takeoverRiskAreaHover,
  riskFoomPreview,
  riskFoomDesign,
  riskFoomTedCaption,
  riskFoomOnPointHover,
  riskSpaceExplorer,
  riskTedGuide,
  riskAmbientDisplay,
  riskHoverDimDisabled,
  highlightLineWhilePointHovered,
  riskZoneDimTween,
  riskZoneDimInverted,
  riskZoneDimDotShrink,
  riskZoneContinuousGuide,
  pointGuideTedDash,
}: ExtrapolatedGraphProps): ExtrapolatedGraphRenderProps => {
  const resolvedScaleSettings = useMemo(
    () => resolveScaleSettings(scaleSettings),
    [scaleSettings],
  )
  const { editorMode: lossOfControlEditorMode } = useLossOfControlEditor()
  const backgroundPatternSettings = useBackgroundPatternSettings()
  // Live-tunable timing/easing (see animationTuningContext): defaults to the
  // saved JSON everywhere, overridden only inside the /hud-studio preview.
  const animationTuning = useAnimationTuning()
  const syncTransitionMs = animationTuning.hudSyncTransitionMs
  // The ⌘+G "Show trailing actor risk" toggle turns on the bottom-anchored
  // black field globally; callers (e.g. the compute-breakdowns figure) can
  // still force it on via the explicit prop.
  const effectiveBlackBelowTooSlowRisk =
    blackBelowTooSlowRisk || backgroundPatternSettings.showTrailingActorRisk
  const [isSafariBrowser, setIsSafariBrowser] = useState(false)

  useEffect(() => {
    setIsSafariBrowser(getIsSafariBrowser())
  }, [])
  const sharedPoints = useMemo(() =>
    toGraphPoints(graphDataFile.shared), [graphDataFile])
  const planSeriesForActivePlan = useMemo(() => {
    if (activePlan === 'B' && graphDataFile.planBUs && graphDataFile.planBUs.length > 0) {
      return graphDataFile.planBUs
    }
    return graphDataFile[PLAN_KEYS[activePlan]] ?? graphDataFile.planD
  }, [activePlan, graphDataFile])
  const planPoints = useMemo(
    () => toGraphPoints(planSeriesForActivePlan),
    [planSeriesForActivePlan],
  )
  const targetGraphPoints = useMemo(
    () => [...sharedPoints, ...planPoints],
    [sharedPoints, planPoints],
  )
  const graphPoints = useTimedAnimatedGraphPoints(
    targetGraphPoints,
    capabilityLineMorphDisabled ? 0 : syncTransitionMs,
    undefined,
    undefined,
    {
      easing: animationTuning.lineMorphEasing,
      valueInterpolation: 'logForLargeChanges',
    },
  )
  const planBChinaTargetGraphPoints = useMemo(() => {
    if (
      activePlan !== 'B' ||
      !showPlanBChinaCapabilityLine ||
      !graphDataFile.planBCh ||
      graphDataFile.planBCh.length === 0
    ) return [] as GraphPoint[]
    return [...sharedPoints, ...toGraphPoints(graphDataFile.planBCh)]
  }, [activePlan, showPlanBChinaCapabilityLine, graphDataFile, sharedPoints])
  const planBChinaAnimatedPoints = useTimedAnimatedGraphPoints(
    planBChinaTargetGraphPoints,
    syncTransitionMs,
    undefined,
    undefined,
    {
      easing: animationTuning.lineMorphEasing,
      valueInterpolation: 'logForLargeChanges',
    },
  )
  const planBChinaRiseEnabled =
    showPlanBChinaCapabilityLine
    && activePlan === 'B'
    && (graphDataFile.planBCh?.length ?? 0) > 0
  const chinaLineRiseProgress = usePlanBChinaLineRiseProgress(
    planBChinaRiseEnabled,
    CHINA_LINE_RISE_DURATION_MS,
  )
  const planBChinaRisenAnimatedPoints = useMemo(
    () => applyChinaLineRiseToGraphPoints(
      planBChinaAnimatedPoints,
      chinaLineRiseProgress,
    ),
    [planBChinaAnimatedPoints, chinaLineRiseProgress],
  )
  const targetLossOfControlMinPoints = useMemo(
    () => toGraphPoints(graphDataFile.lossOfControlByPlan[activePlan].min),
    [graphDataFile, activePlan],
  )
  const targetLossOfControlMaxPoints = useMemo(
    () => toGraphPoints(graphDataFile.lossOfControlByPlan[activePlan].max),
    [graphDataFile, activePlan],
  )
  // The red-above-loss-of-control overlay and gradient strip trace these
  // points, so the band reshapes on the same clock AND curve as the
  // capability-line morph above (shared hudSyncTransitionMs + lineMorphEasing;
  // the choose-your-path arrow highlights read the same pair).
  const lossOfControlMinPoints = useTimedAnimatedGraphPoints(
    targetLossOfControlMinPoints,
    syncTransitionMs,
    undefined,
    undefined,
    { easing: animationTuning.lineMorphEasing },
  )
  const lossOfControlMaxPoints = useTimedAnimatedGraphPoints(
    targetLossOfControlMaxPoints,
    syncTransitionMs,
    undefined,
    undefined,
    { easing: animationTuning.lineMorphEasing },
  )

  // Empty fallback keeps the hooks below in a stable call order.
  const tooSlowRiskBand = graphDataFile.tooSlowRiskByPlan?.[activePlan]
  const targetTooSlowRiskMinPoints = useMemo(
    () => (tooSlowRiskBand
      ? clipGraphPointsBefore(toGraphPoints(tooSlowRiskBand.min), TRAILING_ACTOR_RISK_START_TIMESTAMP)
      : []),
    [tooSlowRiskBand],
  )
  const targetTooSlowRiskMaxPoints = useMemo(
    () => (tooSlowRiskBand
      ? clipGraphPointsBefore(toGraphPoints(tooSlowRiskBand.max), TRAILING_ACTOR_RISK_START_TIMESTAMP)
      : []),
    [tooSlowRiskBand],
  )
  const tooSlowRiskMinPoints = useTimedAnimatedGraphPoints(
    targetTooSlowRiskMinPoints,
    syncTransitionMs,
    undefined,
    undefined,
    { easing: animationTuning.lineMorphEasing },
  )
  const tooSlowRiskMaxPoints = useTimedAnimatedGraphPoints(
    targetTooSlowRiskMaxPoints,
    syncTransitionMs,
    undefined,
    undefined,
    { easing: animationTuning.lineMorphEasing },
  )

  // Never let the graph's "now" fall before the timeline's start (e.g. 2026),
  // so the left edge / current marker stays at the start while the reader is
  // still in the earlier (pre-start) part of the scroll.
  const timelineStartTimestamp = resolvedScaleSettings.timeline.fixedStartTimestamp ?? -Infinity
  const currentDateTimestamp = Math.max(currentDate.getTime(), timelineStartTimestamp)
  const timelineTimestamp = Math.max((targetDate ?? currentDate).getTime(), timelineStartTimestamp)
  const animatedCurrentTimestamp = useAnimatedTimestamp(
    currentDateTimestamp,
    resolvedScaleSettings.animation,
  )
  const currentTimestamp = Math.max(
    targetDate ? currentDateTimestamp : animatedCurrentTimestamp,
    timelineStartTimestamp,
  )
  const currentPoint = useMemo(
    () => createCurrentPoint(currentTimestamp, graphPoints),
    [currentTimestamp, graphPoints],
  )
  const timelineGraphPoints = graphPoints
  const { domainEnd, effectiveCappedTimestamp } = useMemo(
    () => getTimelineBounds(
      timelineGraphPoints,
      timelineTimestamp,
      showGalaxy,
      resolvedScaleSettings.timeline,
    ),
    [timelineGraphPoints, timelineTimestamp, showGalaxy, resolvedScaleSettings.timeline],
  )
  const defaultVisibleGraphState = useMemo(
    () => getVisibleGraphState(
      graphPoints,
      currentPoint,
      currentTimestamp,
      effectiveCappedTimestamp,
      resolvedScaleSettings.timeline.fixedStartTimestamp,
    ),
    [graphPoints, currentPoint, currentTimestamp, effectiveCappedTimestamp, resolvedScaleSettings.timeline.fixedStartTimestamp],
  )
  const planBChinaOverlayLines = useMemo(() => {
    if (planBChinaAnimatedPoints.length === 0) {
      return {
        chinaExtentGraphPoints: [] as GraphPoint[],
        chinaLineSources: [] as GraphLineSource[],
      }
    }
    const chinaCurrentPointForExtent = createCurrentPoint(
      currentTimestamp,
      planBChinaAnimatedPoints,
    )
    const chinaVisibleForExtent = getVisibleGraphState(
      planBChinaAnimatedPoints,
      chinaCurrentPointForExtent,
      currentTimestamp,
      effectiveCappedTimestamp,
      resolvedScaleSettings.timeline.fixedStartTimestamp,
    )
    const chinaExtentGraphPoints = chinaVisibleForExtent.visibleGraphPoints
    const chinaCurrentPointRisen = createCurrentPoint(
      currentTimestamp,
      planBChinaRisenAnimatedPoints,
    )
    const chinaVisibleRisen = getVisibleGraphState(
      planBChinaRisenAnimatedPoints,
      chinaCurrentPointRisen,
      currentTimestamp,
      effectiveCappedTimestamp,
      resolvedScaleSettings.timeline.fixedStartTimestamp,
    )
    const chinaRenderDefaults = createDefaultRenderSourceState(chinaVisibleRisen)
    const chinaLineSources: GraphLineSource[] = chinaRenderDefaults.lineSources.map(lineSource => ({
      ...lineSource,
      key: lineSource.key === 'past' ? 'plan-b-china-past' : 'plan-b-china-future',
      stroke: PLAN_B_CHINA_CAPABILITY_LINE_COLOR,
    }))
    return {
      chinaExtentGraphPoints,
      chinaLineSources,
    }
  }, [
    planBChinaAnimatedPoints,
    planBChinaRisenAnimatedPoints,
    currentTimestamp,
    effectiveCappedTimestamp,
    resolvedScaleSettings.timeline.fixedStartTimestamp,
  ])
  // Visual-only floor for the capability line's rendered geometry (see the
  // capabilityLineFloorValue prop). Applied to the line/marker sources and to
  // primaryVisibleGraphPoints (which drives the dither carve-out, the red
  // gradient strip and the hover geometry) so everything tracks the drawn
  // line; the underlying data series is untouched.
  const clampToCapabilityLineFloor = useCallback(
    (points: GraphPoint[]): GraphPoint[] =>
      capabilityLineFloorValue === undefined
        ? points
        : points.map(point =>
          point.value < capabilityLineFloorValue
            ? { ...point, value: capabilityLineFloorValue }
            : point
        ),
    [capabilityLineFloorValue],
  )
  const capabilityLineSolidColor = backgroundPatternSettings.capabilityLineSolidColor
  const capabilityLineDashedColor = backgroundPatternSettings.capabilityLineDashedColor
  const capabilityLineStrokeWidth = backgroundPatternSettings.capabilityLineStrokeWidth
  const capabilityFutureLineStrokeWidth = backgroundPatternSettings.capabilityFutureLineStrokeWidth
  const capabilityFutureLineDashed = backgroundPatternSettings.capabilityFutureLineDashed
  const capabilityFutureLineDashLength = backgroundPatternSettings.capabilityFutureLineDashLength
  const baseRenderSourceState = useMemo(
    () => {
      const main = createDefaultRenderSourceState(defaultVisibleGraphState)
      // Recolor + resize the capability line's solid (past) and dashed (future)
      // segments — plus the future markers — from the live ⌘+G settings. Mirrors
      // the china-line stroke override above and keeps
      // createDefaultRenderSourceState pure. The per-graph
      // capabilityPastLineColor prop beats the settings' solid color, and also
      // fills the current-point marker so the endpoint dot matches the line;
      // capabilityDashedLineColor does the same for the dashed segment.
      const recoloredMain = {
        ...main,
        lineSources: main.lineSources.map((lineSource) =>
          lineSource.key === 'past'
            ? {
              ...lineSource,
              graphPoints: clampToCapabilityLineFloor(lineSource.graphPoints),
              stroke: capabilityPastLineColor ?? capabilityLineSolidColor,
              strokeWidth: capabilityPastLineStrokeWidth ?? capabilityLineStrokeWidth,
            }
            : {
              ...lineSource,
              graphPoints: clampToCapabilityLineFloor(lineSource.graphPoints),
              stroke: capabilityDashedLineColor ?? capabilityLineDashedColor,
              strokeWidth: capabilityFutureLineStrokeWidth,
              strokeDasharray: capabilityFutureLineDashed
                ? `${capabilityFutureLineDashLength},${CAPABILITIES_FUTURE_LINE_DASH_GAP}`
                : undefined,
            }
        ),
        futureMarkerSources: main.futureMarkerSources.map((markerSource) => ({
          ...markerSource,
          graphPoints: clampToCapabilityLineFloor(markerSource.graphPoints),
          stroke: capabilityDashedLineColor ?? capabilityLineDashedColor,
        })),
        currentMarkerSources:
          capabilityPastLineColor
            || currentPointMarkerRadius !== undefined
            || capabilityLineFloorValue !== undefined
            ? main.currentMarkerSources.map((markerSource) => ({
              ...markerSource,
              graphPoints: clampToCapabilityLineFloor(markerSource.graphPoints),
              fill: capabilityPastLineColor ?? markerSource.fill,
              r: currentPointMarkerRadius ?? markerSource.r,
            }))
            : main.currentMarkerSources,
      }
      if (planBChinaOverlayLines.chinaLineSources.length === 0) return recoloredMain
      return {
        ...recoloredMain,
        lineSources: [...planBChinaOverlayLines.chinaLineSources, ...recoloredMain.lineSources],
        visibleGraphPoints: [
          ...planBChinaOverlayLines.chinaExtentGraphPoints,
          ...recoloredMain.visibleGraphPoints,
        ],
      }
    },
    [
      defaultVisibleGraphState,
      planBChinaOverlayLines,
      capabilityPastLineColor,
      capabilityDashedLineColor,
      capabilityPastLineStrokeWidth,
      capabilityLineFloorValue,
      clampToCapabilityLineFloor,
      currentPointMarkerRadius,
      capabilityLineSolidColor,
      capabilityLineDashedColor,
      capabilityLineStrokeWidth,
      capabilityFutureLineStrokeWidth,
      capabilityFutureLineDashed,
      capabilityFutureLineDashLength,
    ],
  )
  const renderSourceState = useMemo(
    () => showLossOfControlLines
      ? appendProjectedLossOfControlRenderSourceState({
        renderSourceState: baseRenderSourceState,
        lossOfControlMinPoints,
        lossOfControlMaxPoints,
        effectiveCappedTimestamp,
        currentTimestamp,
      })
      : baseRenderSourceState,
    [
      baseRenderSourceState,
      showLossOfControlLines,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      effectiveCappedTimestamp,
      currentTimestamp,
    ],
  )

  const padding = useMemo(
    () => ({
      ...resolvedScaleSettings.padding,
      left: scaleSettings?.padding?.left ?? 3,
    }),
    [resolvedScaleSettings.padding, scaleSettings?.padding?.left],
  )
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom
  const maxVisibleValue = Math.max(
    ...renderSourceState.visibleGraphPoints.map(graphPoint => graphPoint.value),
  )
  const futureGraphPoints = renderSourceState.futureMarkerSources.flatMap(markerSource =>
    markerSource.graphPoints
  )
  const maxFutureValue = futureGraphPoints.length > 0
    ? Math.max(...futureGraphPoints.map(graphPoint => graphPoint.value))
    : maxVisibleValue
  const currentValueForDomain = renderSourceState.currentMarkerSources[0]?.graphPoints[0]?.value
    ?? maxVisibleValue
  const targetYDomainMax = getYDomainMax(
    currentValueForDomain,
    maxVisibleValue,
    maxFutureValue,
    resolvedScaleSettings.yAxis,
  )
  // While the plan-preview fan is mounted, every fan line's visible data
  // points join the axis anchors below. The anchors are otherwise only the
  // ACTIVE plan's point grid, and the plans' grids differ (Plan A's 4-month
  // steps vs the branch plans' 0.1-year phases vs Plan S's whole years), so a
  // hover's activePlan swap would re-space the evenly-by-index time→x map and
  // subtly reshape the whole fan even under an identical camera (same yMax /
  // end year). The union grid is the same for every hover state, so the
  // choice-point frame holds still.
  const planPreviewAxisTimestamps = useMemo(
    () => {
      if (!planPreviewLines || planPreviewLines.length === 0) return []
      const fixedStartTimestamp = resolvedScaleSettings.timeline.fixedStartTimestamp
      return planPreviewLines.flatMap(previewLine =>
        toGraphPoints(previewLine.data)
          .map(graphPoint => graphPoint.timestamp)
          .filter(timestamp =>
            (fixedStartTimestamp === undefined || timestamp >= fixedStartTimestamp)
            && timestamp <= effectiveCappedTimestamp
          )
      )
    },
    [
      planPreviewLines,
      resolvedScaleSettings.timeline.fixedStartTimestamp,
      effectiveCappedTimestamp,
    ],
  )
  const computedVisibleDataPointTimestamps = useMemo(
    () => {
      const mainLineTimestamps = getVisibleDataPointTimestampsForAxis(renderSourceState)
      if (planPreviewAxisTimestamps.length === 0) return mainLineTimestamps
      return Array.from(new Set([...mainLineTimestamps, ...planPreviewAxisTimestamps]))
        .sort((timestampA, timestampB) => timestampA - timestampB)
    },
    [renderSourceState, planPreviewAxisTimestamps],
  )
  const visibleDataPointTimestamps = useStableNumberArray(
    computedVisibleDataPointTimestamps,
  )
  const targetTimestampPositions = useMemo(
    () => createEvenlySpacedTimestampPositions({
      visibleTimestamps: visibleDataPointTimestamps,
      padding,
      innerWidth,
    }),
    [visibleDataPointTimestamps, padding, innerWidth],
  )
  const graphViewport = useTimedGraphViewport({
    targetYDomainMax,
    targetTimestampPositions,
    durationMs: syncTransitionMs,
    ySnapThreshold: 0.25,
  })
  const yDomainMax = graphViewport.yDomainMax
  const animatedTimestampPositions = graphViewport.timestampPositions
  // While a dropped rightmost ghost slides off, hold the visual cap at
  // it so right-edge visuals stay pinned instead of snapping inward.
  const lastAnimatedTimestampPosition =
    animatedTimestampPositions[animatedTimestampPositions.length - 1]
  const animatedEffectiveCappedTimestamp =
    lastAnimatedTimestampPosition !== undefined
      && lastAnimatedTimestampPosition.timestamp > effectiveCappedTimestamp
      ? lastAnimatedTimestampPosition.timestamp
      : effectiveCappedTimestamp
  // Re-inject the ghost timestamp's data so the line endpoint slides
  // off with it instead of snapping back to the new rightmost point.
  const ghostInjectedRenderSourceState = useMemo(
    () => {
      const visibleDataPointTimestampSet = new Set(visibleDataPointTimestamps)
      const ghostTimestamps = animatedTimestampPositions
        .map(animatedPosition => animatedPosition.timestamp)
        .filter(timestamp => !visibleDataPointTimestampSet.has(timestamp))
      return buildGhostInjectedRenderSourceState({
        baseRenderSourceState: renderSourceState,
        ghostTimestamps,
        ghostDataSourcesByLineKey: {
          'future': graphPoints,
          'projected-loss-of-control': lossOfControlMinPoints,
          'projected-loss-of-control-triple': lossOfControlMaxPoints,
          'plan-b-china-future': planBChinaRisenAnimatedPoints,
        },
      })
    },
    [
      renderSourceState,
      animatedTimestampPositions,
      visibleDataPointTimestamps,
      graphPoints,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      planBChinaRisenAnimatedPoints,
    ],
  )

  const xForTimestamp = useMemo(
    () => createXForTimestamp({
      showGalaxy,
      padding,
      innerWidth,
      galaxySettings: resolvedScaleSettings.galaxy,
      timestampPositions: animatedTimestampPositions,
    }),
    [
      showGalaxy,
      padding,
      innerWidth,
      resolvedScaleSettings.galaxy,
      animatedTimestampPositions,
    ],
  )
  const yDomainMin = resolvedScaleSettings.yAxis.fixedMin ?? 0
  const yForValue = useMemo(
    () => createYForValue({
      padding,
      innerHeight,
      yDomainMax,
      yDomainMin,
      useLogScale,
    }),
    [padding, innerHeight, yDomainMax, yDomainMin, useLogScale],
  )
  const valueForY = useMemo(
    () => createValueForY({
      padding,
      innerHeight,
      yDomainMax,
      yDomainMin,
      useLogScale,
    }),
    [padding, innerHeight, yDomainMax, yDomainMin, useLogScale],
  )
  // Derive from the (start-clamped) currentTimestamp, not the raw currentDate,
  // so the current-year axis tick never predates the timeline start (e.g. it
  // reads 2026 rather than 2025 while the reader is still at the very top).
  const currentYear = new Date(currentTimestamp).getUTCFullYear()
  // UTC like currentYear, so the pill's month and year always agree.
  const currentMonthIndex = new Date(currentTimestamp).getUTCMonth()
  const leftTimestamp = visibleDataPointTimestamps[0] ?? currentTimestamp
  const leftYear = new Date(leftTimestamp).getUTCFullYear()
  const rightTimestamp =
    visibleDataPointTimestamps[visibleDataPointTimestamps.length - 1]
    ?? domainEnd
  const rightYear = new Date(rightTimestamp).getUTCFullYear()
  const currentTimelineX = xForTimestamp(currentTimestamp)
  const graphLineSmoothing = backgroundPatternSettings.capabilityLineSmoothing
  const flattenSmoothedGraphLines = isSafariBrowser
  // Clamp path geometry near the canvas so off-graph points (plan
  // hover, lagging y-domain) keep animating instead of trailing off.
  const lineRenderBounds = useMemo(
    () => {
      const verticalOverflow = Math.max(
        LINE_RENDER_MIN_OVERFLOW_PX,
        height * LINE_RENDER_VERTICAL_OVERFLOW_RATIO
      )
      return {
        minX: -LINE_RENDER_MIN_OVERFLOW_PX,
        maxX: width + LINE_RENDER_MIN_OVERFLOW_PX,
        minY: -verticalOverflow,
        maxY: height + verticalOverflow,
      }
    },
    [height, width],
  )
  // Static (per-frame-stable) point series for the plan-preview fan; the
  // floor clamp mirrors the main line's so the active plan's twin can't poke
  // out below it at the left tail.
  const planPreviewLineTargets = useMemo(
    () => (planPreviewLines ?? []).map(previewLine => ({
      key: previewLine.key,
      graphPoints: clampToCapabilityLineFloor(toGraphPoints(previewLine.data)),
    })),
    [planPreviewLines, clampToCapabilityLineFloor],
  )
  // The fan's past/future split date: normally the current date, held at its
  // last unfrozen value while planPreviewSplitFrozen (the branch-hover
  // autoplay sweep) so the fan lines' solid fronts stay put.
  const lastUnfrozenPlanPreviewSplitRef = useRef(currentTimestamp)
  if (!planPreviewSplitFrozen) {
    lastUnfrozenPlanPreviewSplitRef.current = currentTimestamp
  }
  const planPreviewSplitTimestamp = planPreviewSplitFrozen
    ? lastUnfrozenPlanPreviewSplitRef.current
    : currentTimestamp
  // The preview fan's line sources plus its dither carve-out polylines. Each
  // line runs the main line's own pipeline (visible-window clip, past/future
  // split at the current point, smoothing, dash settings), so the active
  // plan's thin twin lands exactly under the main line — down to the dash
  // phase — and swapping activePlan reads as emphasis moving across
  // already-drawn lines.
  const planPreviewOverlay = useMemo(
    () => {
      const fixedStartTimestamp = resolvedScaleSettings.timeline.fixedStartTimestamp
      const perLine = planPreviewLineTargets.map(target => {
        const lineCurrentPoint = createCurrentPoint(planPreviewSplitTimestamp, target.graphPoints)
        const visibleState = getVisibleGraphState(
          target.graphPoints,
          lineCurrentPoint,
          planPreviewSplitTimestamp,
          effectiveCappedTimestamp,
          fixedStartTimestamp,
        )
        const lineOpacity = planPreviewLinesOpacity
          * (target.key === planPreviewEmphasizedLineKey
            ? 1
            : planPreviewDeemphasizedLinesOpacity)
        // Dash→dot for this line (see planPreviewDotProgressByLineKey): the
        // dash shrinks in place toward the dot mark while the gap absorbs
        // the difference, so the dash period — and with it the spacing
        // between dashes — never changes. The linear tween is bent onto the
        // hover's shared S-curve (lineMorphEasing), the same curve the line
        // morph and red-band reshape ride, so all three move as one.
        const dotProgress = easeTimedGraphPointProgress(
          planPreviewDotProgressByLineKey?.[target.key] ?? 0,
          animationTuning.lineMorphEasing,
        )
        const dashLength = capabilityFutureLineDashLength
          + (PLAN_PREVIEW_DOT_DASH_LENGTH_PX - capabilityFutureLineDashLength) * dotProgress
        const dashGap =
          capabilityFutureLineDashLength + CAPABILITIES_FUTURE_LINE_DASH_GAP - dashLength
        const lineSources = createDefaultRenderSourceState(visibleState).lineSources
          .map(lineSource => ({
            ...lineSource,
            key: `${target.key}-${lineSource.key}`,
            stroke: PLAN_PREVIEW_LINE_COLOR,
            strokeWidth: PLAN_PREVIEW_LINE_STROKE_WIDTH_PX,
            strokeOpacity: lineOpacity,
            strokeDasharray:
              lineSource.key === 'future' && capabilityFutureLineDashed
                ? `${dashLength},${dashGap}`
                : undefined,
          }))
        const carvePoints = getPrimaryVisibleGraphPoints({
          graphPoints: target.graphPoints,
          currentTimestamp: planPreviewSplitTimestamp,
          effectiveCappedTimestamp: animatedEffectiveCappedTimestamp,
        })
        const carvePolyline = carvePoints.length >= 2
          ? createSmoothedScreenPoints(
            carvePoints.map(graphPoint => {
              const x = xForTimestamp(graphPoint.timestamp)
              const y = yForValue(graphPoint.value)
              return {
                x: Math.max(
                  lineRenderBounds.minX,
                  Math.min(lineRenderBounds.maxX, x),
                ),
                y: Math.max(
                  lineRenderBounds.minY,
                  Math.min(lineRenderBounds.maxY, y),
                ),
              }
            }),
            backgroundPatternSettings.capabilityLineSmoothing,
          )
          : null
        // Small label at the line's drawn end (the last knot inside the
        // visible window — futureLinePoints stops there), held clear of the
        // stroke: the label's bottom-right corner sits 10px above and 10px
        // left of the line. The y is clamped into the plot for lines that
        // have exited the top of the frame; the x then re-anchors to where
        // the line crosses the label's bottom edge, because at that height a
        // steep line sits left of its last knot and the knot's own x would
        // put the text on top of the stroke.
        const endLabel = planPreviewEndLabelByLineKey?.[target.key]
        const futureScreenPoints = visibleState.futureLinePoints.map(graphPoint => ({
          x: xForTimestamp(graphPoint.timestamp),
          y: yForValue(graphPoint.value),
        }))
        const lineEndScreenPoint = futureScreenPoints[futureScreenPoints.length - 1]
        // The flat box sits on the dot dither, whose rows live on an absolute
        // grid (tile origins at multiples of tileSize, dots centered in their
        // tile and never wider than it — see forEachDotTileOrigin). Snapping
        // the box's bottom edge to that grid and making its height a whole
        // number of rows keeps both horizontal edges in the gaps between dot
        // rows, so the box never crops a dot into a sliver.
        const ditherTileSize =
          2 * Math.max(1, Math.round(backgroundPatternSettings.patternScale))
        const endLabelBoxHeight = Math.ceil(
          PLAN_PREVIEW_END_LABEL_MIN_BOX_HEIGHT_PX / ditherTileSize
        ) * ditherTileSize
        const endLabelBottomY = lineEndScreenPoint
          ? Math.round(
            Math.min(
              Math.max(
                lineEndScreenPoint.y - PLAN_PREVIEW_END_LABEL_LINE_CLEARANCE_PX,
                padding.top + PLAN_PREVIEW_END_LABEL_MIN_Y_PX,
              ),
              height - padding.bottom,
            ) / ditherTileSize,
          ) * ditherTileSize
          : 0
        const endLabelAnnotation = endLabel && lineEndScreenPoint
          ? {
            key: `${target.key}-end-label`,
            x: (findLineXAtY(futureScreenPoints, endLabelBottomY) ?? lineEndScreenPoint.x)
              - PLAN_PREVIEW_END_LABEL_LINE_CLEARANCE_PX,
            y: endLabelBottomY,
            text: endLabel.text,
            transform: 'translate(-100%, -100%)',
            opacity: endLabel.opacity,
            transitionDurationMs: endLabel.transitionDurationMs,
            pointerEvents: 'none' as const,
            fontSize: PLAN_PREVIEW_END_LABEL_FONT_SIZE_PX,
            flatBackground: true,
            height: endLabelBoxHeight,
          }
          : null
        return { lineSources, carvePolyline, endLabelAnnotation }
      })
      return {
        lineSources: perLine.flatMap(line => line.lineSources),
        carvePolylines: perLine.flatMap(line =>
          line.carvePolyline ? [line.carvePolyline] : []
        ),
        endLabelAnnotations: perLine.flatMap(line =>
          line.endLabelAnnotation ? [line.endLabelAnnotation] : []
        ),
      }
    },
    [
      planPreviewLineTargets,
      planPreviewLinesOpacity,
      planPreviewDeemphasizedLinesOpacity,
      planPreviewEmphasizedLineKey,
      planPreviewDotProgressByLineKey,
      planPreviewEndLabelByLineKey,
      animationTuning.lineMorphEasing,
      planPreviewSplitTimestamp,
      effectiveCappedTimestamp,
      animatedEffectiveCappedTimestamp,
      resolvedScaleSettings.timeline.fixedStartTimestamp,
      capabilityFutureLineDashed,
      capabilityFutureLineDashLength,
      xForTimestamp,
      yForValue,
      lineRenderBounds,
      padding,
      height,
      backgroundPatternSettings.capabilityLineSmoothing,
      backgroundPatternSettings.patternScale,
    ],
  )
  // Capability-line sources are keyed 'past'/'future'/'shared-*' (all
  // but the China overlay); drop them per the hide* flags. The plan-preview
  // fan goes FIRST so it paints under the main capability line.
  const visibleLineSources = useMemo(() => {
    const sources = [
      ...planPreviewOverlay.lineSources,
      ...ghostInjectedRenderSourceState.lineSources,
    ]
    if (hideCapabilityLine) return sources.filter(s => s.key.includes('china'))
    if (hideCapabilityFutureLine) {
      return sources.filter(s => !s.key.includes('future') || s.key.includes('china'))
    }
    return sources
  }, [
    ghostInjectedRenderSourceState.lineSources,
    planPreviewOverlay.lineSources,
    hideCapabilityLine,
    hideCapabilityFutureLine,
  ])
  const renderedLineSegments = useMemo(
    () => createRenderedGraphLineSegments({
      lineSources: visibleLineSources,
      futureMarkerSources: ghostInjectedRenderSourceState.futureMarkerSources,
      xForTimestamp,
      yForValue,
      showYearDots,
      lineSmoothing: graphLineSmoothing,
      flattenSmoothedCurves: flattenSmoothedGraphLines,
      lineRenderBounds,
    }),
    [
      visibleLineSources,
      ghostInjectedRenderSourceState.futureMarkerSources,
      xForTimestamp,
      yForValue,
      showYearDots,
      graphLineSmoothing,
      flattenSmoothedGraphLines,
      lineRenderBounds,
    ],
  )
  const isMarkerWithinContainerHeight = (marker: { cy: number }) =>
    marker.cy >= 0 && marker.cy <= height
  const futureMarkers = createRenderedMarkers(
    ghostInjectedRenderSourceState.futureMarkerSources,
    xForTimestamp,
    yForValue,
  ).filter(isMarkerWithinContainerHeight)
  const pastMarkers = createRenderedMarkers(
    renderSourceState.pastMarkerSources,
    xForTimestamp,
    yForValue,
  ).filter(isMarkerWithinContainerHeight)
  const visiblePastMarkers = useMemo(
    () => pastMarkers.filter(marker =>
      !marker.key.startsWith('projected-loss-of-control')
    ),
    [pastMarkers],
  )
  const unculledCurrentMarkers = createRenderedMarkers(
    renderSourceState.currentMarkerSources,
    xForTimestamp,
    yForValue,
  )
  const currentMarkers = unculledCurrentMarkers.filter(isMarkerWithinContainerHeight)
  // Year-pill drop anchor: the y where the pill's dropped state should rest.
  // The dropped pill is flush-LEFT (epilogue mode), so it must ride the
  // capability line's LEFT end — not the current/rightmost point, which rides
  // up to the top of the graph as the curve goes superintelligent and would
  // drag the pill to the top with it. See capabilityLineScreenPoints below.
  const primaryVisibleGraphPoints = useMemo(
    () => clampToCapabilityLineFloor(getPrimaryVisibleGraphPoints({
      graphPoints,
      currentTimestamp,
      effectiveCappedTimestamp: animatedEffectiveCappedTimestamp,
    })),
    [
      graphPoints,
      currentTimestamp,
      animatedEffectiveCappedTimestamp,
      clampToCapabilityLineFloor,
    ],
  )
  const capabilityLineScreenPoints = useMemo(
    () => {
      // Drop exclusion points for hidden lines so dot-exclusion doesn't
      // leave a line-shaped gap where nothing is drawn.
      const exclusionPoints = hideCapabilityLine
        ? []
        : hideCapabilityFutureLine
          ? primaryVisibleGraphPoints.filter(p => p.timestamp <= currentTimestamp)
          : primaryVisibleGraphPoints
      return createSmoothedScreenPoints(
        exclusionPoints.map(graphPoint => {
          const x = xForTimestamp(graphPoint.timestamp)
          const y = yForValue(graphPoint.value)
          return {
            x: Math.max(
              lineRenderBounds.minX,
              Math.min(lineRenderBounds.maxX, x),
            ),
            y: Math.max(
              lineRenderBounds.minY,
              Math.min(lineRenderBounds.maxY, y),
            ),
          }
        }),
        backgroundPatternSettings.capabilityLineSmoothing
      )
    },
    [
      primaryVisibleGraphPoints,
      hideCapabilityLine,
      hideCapabilityFutureLine,
      currentTimestamp,
      xForTimestamp,
      yForValue,
      lineRenderBounds,
      backgroundPatternSettings.capabilityLineSmoothing,
    ],
  )
  // The leftmost capability-line point's y, clamped into the graph box. In the
  // epilogue viewport the line's left end rests near the graph bottom, which is
  // where the flush-left year pill should drop to. Fall back to the graph
  // bottom when there's no line to read.
  const currentYearPillAnchorY = capabilityLineScreenPoints[0] !== undefined
    ? Math.min(Math.max(capabilityLineScreenPoints[0].y, 0), height)
    : height
  const planBChinaPrimaryVisibleRisen = useMemo(
    () =>
      showPlanBChinaCapabilityLine
      && activePlan === 'B'
      && planBChinaRisenAnimatedPoints.length > 0
        ? getPrimaryVisibleGraphPoints({
          graphPoints: planBChinaRisenAnimatedPoints,
          currentTimestamp,
          effectiveCappedTimestamp: animatedEffectiveCappedTimestamp,
        })
        : [],
    [
      showPlanBChinaCapabilityLine,
      activePlan,
      planBChinaRisenAnimatedPoints,
      currentTimestamp,
      animatedEffectiveCappedTimestamp,
    ],
  )
  const additionalCapabilityLinePolylines = useMemo(
    () =>
      planBChinaPrimaryVisibleRisen.length >= 2
        ? [
          createSmoothedScreenPoints(
            planBChinaPrimaryVisibleRisen.map(graphPoint => {
              const x = xForTimestamp(graphPoint.timestamp)
              const y = yForValue(graphPoint.value)
              return {
                x: Math.max(
                  lineRenderBounds.minX,
                  Math.min(lineRenderBounds.maxX, x),
                ),
                y: Math.max(
                  lineRenderBounds.minY,
                  Math.min(lineRenderBounds.maxY, y),
                ),
              }
            }),
            backgroundPatternSettings.capabilityLineSmoothing,
          ),
        ]
        : [],
    [
      planBChinaPrimaryVisibleRisen,
      xForTimestamp,
      yForValue,
      lineRenderBounds,
      backgroundPatternSettings.capabilityLineSmoothing,
    ],
  )
  // The plan-preview fan's carve rides the fan's fade — its strength is the
  // fan's line opacity, so the dither channels melt in/out in step with the
  // lines instead of popping. Kept separate from the full-strength
  // additionalCapabilityLinePolylines (the China overlay line). The
  // de-emphasis fade multiplies in: as the non-hovered lines melt away their
  // channels close with them, and the emphasized line's path keeps its
  // channel via the main line's own deeper carve.
  const planPreviewCarve = useMemo<FadingCapabilityLinePolylines | null>(
    () => (planPreviewOverlay.carvePolylines.length === 0
      ? null
      : {
          polylines: planPreviewOverlay.carvePolylines,
          strength: planPreviewLinesOpacity * planPreviewDeemphasizedLinesOpacity,
        }),
    [
      planPreviewOverlay.carvePolylines,
      planPreviewLinesOpacity,
      planPreviewDeemphasizedLinesOpacity,
    ],
  )
  const redAboveLossOfControlPathRender = useMemo(
    () => createRedAboveLossOfControlPathRender({
      show: showLossOfControlLines,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      effectiveCappedTimestamp: animatedEffectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
    }),
    [
      showLossOfControlLines,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      animatedEffectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
    ],
  )
  // Horizontal extent of the plot area, in graph coordinates. The dot dither
  // lays its columns out as an exact fit across this span so the first/last
  // dots sit flush with the plot edges instead of being clipped there.
  const plotSpanX = useMemo(
    () => ({ left: padding.left, right: width - padding.right }),
    [padding, width],
  )
  // screen-x → story year, from the timeline (xForTimestamp is monotonic in
  // year). Shared by both the red and black field inspectors/shade fields.
  const yearForScreenX = useMemo(() => {
    const yearAnchors = TRAILING_ACTOR_RISK_YEARS.map((year) => ({
      year,
      x: xForTimestamp(Date.UTC(year, 0, 1)),
    }))
    return (screenX: number): number => {
      const last = yearAnchors[yearAnchors.length - 1]
      if (screenX <= yearAnchors[0].x) return yearAnchors[0].year
      if (screenX >= last.x) return last.year
      for (let i = 0; i < yearAnchors.length - 1; i += 1) {
        if (screenX <= yearAnchors[i + 1].x) {
          const span = yearAnchors[i + 1].x - yearAnchors[i].x
          const t = span > 0 ? (screenX - yearAnchors[i].x) / span : 0
          return yearAnchors[i].year + t * (yearAnchors[i + 1].year - yearAnchors[i].year)
        }
      }
      return last.year
    }
  }, [xForTimestamp])
  // The red field's "confidence" is the geometric risk position: 0 at the loss-
  // of-control lower line, 1 at the upper line (there's no tabulated takeover
  // CDF). Its density is mapped from that via the red confidence→density stops.
  const redRiskProgressAtPoint = useMemo(() => {
    const pathRender = redAboveLossOfControlPathRender
    if (!pathRender) return null
    // Cached lookup (binary search + per-x memo): this runs per TILE per dither
    // redraw, and the uncached variant's linear polyline scans made the red
    // field draw ~25x more expensive than the black one.
    const progressAtPoint = createRawProgressAtPointLookup(
      pathRender.minLineScreenPoints,
      pathRender.maxLineScreenPoints,
      'top',
    )
    return (screenX: number, screenY: number): number | null =>
      progressAtPoint({ x: screenX, y: screenY })
  }, [redAboveLossOfControlPathRender])
  const redRiskShadeField = useMemo<TileShadeField | null>(() => {
    if (!redAboveLossOfControl || !redRiskProgressAtPoint) return null
    const stops = [...backgroundPatternSettings.redRiskConfidenceStops]
      .sort((a, b) => a.confidence - b.confidence)
    const progressAt = redRiskProgressAtPoint
    return (screenX, screenY) => {
      const progress = progressAt(screenX, screenY)
      if (progress === null) return 0
      return densityFromConfidenceStops(progress, stops)
    }
  }, [redAboveLossOfControl, redRiskProgressAtPoint, backgroundPatternSettings.redRiskConfidenceStops])
  const redRiskDotRadiusCapDensity = useMemo(
    () => Math.max(0, ...backgroundPatternSettings.redRiskConfidenceStops.map((s) => s.density)),
    [backgroundPatternSettings.redRiskConfidenceStops],
  )
  const redAboveLossOfControlCanvasLayer = useMemo(
    () => {
      if (!redAboveLossOfControl) return undefined
      return createRedAboveLossOfControlDitherCanvasLayer({
        // Density from the red risk→density stops (see redRiskShadeField), with
        // the dot-radius cap raised to the stops' peak so it isn't clipped.
        pathRender: redAboveLossOfControlPathRender,
        settings: { ...backgroundPatternSettings, maxDitherDensity: redRiskDotRadiusCapDensity },
        faded: redAboveLossOfControlFaded,
        capabilityLineScreenPoints,
        additionalCapabilityLineScreenPointsList:
          additionalCapabilityLinePolylines,
        fadingCapabilityLinePolylines: planPreviewCarve,
        plotSpanX,
        tileShadeField: redRiskShadeField,
        shadeModel: redRiskShadeField
          ? {
              kind: 'progressStops',
              stops: backgroundPatternSettings.redRiskConfidenceStops,
            }
          : null,
      }) ?? undefined
    },
    [
      redAboveLossOfControl,
      redAboveLossOfControlPathRender,
      backgroundPatternSettings,
      redRiskDotRadiusCapDensity,
      redRiskShadeField,
      redAboveLossOfControlFaded,
      capabilityLineScreenPoints,
      additionalCapabilityLinePolylines,
      planPreviewCarve,
      plotSpanX,
    ],
  )
  // floorY = screen-y of the plot floor, anchoring the black field.
  const tooSlowRiskFloorY = padding.top + innerHeight
  const blackBelowTooSlowRiskPathRender = useMemo(
    () => createRedAboveLossOfControlPathRender({
      show: effectiveBlackBelowTooSlowRisk && tooSlowRiskBand !== undefined,
      lossOfControlMinPoints: tooSlowRiskMinPoints,
      lossOfControlMaxPoints: tooSlowRiskMaxPoints,
      effectiveCappedTimestamp: animatedEffectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
      anchor: 'bottom',
      floorY: tooSlowRiskFloorY,
    }),
    [
      effectiveBlackBelowTooSlowRisk,
      tooSlowRiskBand,
      tooSlowRiskMinPoints,
      tooSlowRiskMaxPoints,
      animatedEffectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
      tooSlowRiskFloorY,
    ],
  )
  // The trailing-actor field's confidence→shade mapping is controlled by its own
  // dither-density + curve settings (⌘+G "Trailing actor" section), independent
  // of the red loss-of-control field. A caller that passes blackPatternSettings
  // — e.g. the standalone takeover-risk figures — sets its own instead.
  const blackBelowTooSlowRiskSettings = useMemo(
    () => blackPatternSettings ?? {
      ...backgroundPatternSettings,
      color: backgroundPatternSettings.trailingActorRiskColor,
      // Plan S's trailing-actor field fades in very light (a faint, gradual
      // possibility) rather than the solid field the racing plans get.
      opacity: activePlan === 'S' ? 0.3 : backgroundPatternSettings.opacity,
      // The field's per-tile density comes from the confidence→density stops
      // (via the shade field below), bypassing the gradient. maxDitherDensity
      // only sets the dot-radius cap here — ride it at the stops' peak density
      // so the densest stop isn't clipped.
      maxDitherDensity: Math.max(
        0,
        ...backgroundPatternSettings.trailingActorRiskConfidenceStops.map((s) => s.density),
      ),
    },
    [blackPatternSettings, backgroundPatternSettings, activePlan],
  )
  // The trailing-actor field's shade is the ACTUAL per-capability confidence
  // from the CDF (not a two-contour band with a synthetic gradient): at each
  // tile, shade = P(the trailing actor reached ≥ this tile's capability by this
  // tile's year), for the deal-survival slice the field conditions on. The
  // min/max/curve settings map that confidence to shade intensity.
  const trailingActorRiskSurvival =
    backgroundPatternSettings.trailingActorRiskConditioningYearOverride != null
      ? survivalYearsForYear(backgroundPatternSettings.trailingActorRiskConditioningYearOverride)
      : survivalYearsForDate(targetDate ?? currentDate)
  const trailingActorRiskShadeField = useMemo<TileShadeField | null>(() => {
    if (!effectiveBlackBelowTooSlowRisk) return null
    // Sort once (stops are stored in author order — see sanitizeConfidenceStops).
    const stops = [...backgroundPatternSettings.trailingActorRiskConfidenceStops]
      .sort((a, b) => a.confidence - b.confidence)
    // The dither raster samples this field at every tile of every redraw, but
    // tiles share their column's year and their row's capability — so resolve
    // each screen coordinate's interpolation params once and cache them here.
    // The caches live inside this memo, so any input change (survival slice,
    // scales) that would invalidate them recreates the closure instead.
    // Racing branches (trailingActorRiskYearShift > 0) sample the CDF that
    // many years LATER in field time (= the deal-world field shifted left on
    // the plot) and pin survival slice 1: no deal is receding their field.
    const evaluator = createTrailingActorRiskConfidenceEvaluator(
      trailingActorRiskYearShift > 0 ? 1 : trailingActorRiskSurvival,
    )
    const yearParamsByScreenX = new Map<number, TrailingActorRiskYearParams>()
    const capParamsByScreenY = new Map<number, TrailingActorRiskCapParams>()
    return (screenX, screenY) => {
      let yearParams = yearParamsByScreenX.get(screenX)
      if (yearParams === undefined) {
        yearParams = evaluator.yearParamsFor(
          yearForScreenX(screenX) + trailingActorRiskYearShift,
        )
        yearParamsByScreenX.set(screenX, yearParams)
      }
      let capParams = capParamsByScreenY.get(screenY)
      if (capParams === undefined) {
        capParams = evaluator.capParamsFor(valueForY(screenY))
        capParamsByScreenY.set(screenY, capParams)
      }
      return densityFromConfidenceStops(
        evaluator.confidenceAt(yearParams, capParams),
        stops,
      )
    }
  }, [
    effectiveBlackBelowTooSlowRisk,
    backgroundPatternSettings.trailingActorRiskConfidenceStops,
    trailingActorRiskSurvival,
    trailingActorRiskYearShift,
    yearForScreenX,
    valueForY,
  ])
  const blackBelowTooSlowRiskCanvasLayer = useMemo(
    () => {
      if (!effectiveBlackBelowTooSlowRisk) return undefined
      return createRedAboveLossOfControlDitherCanvasLayer({
        pathRender: blackBelowTooSlowRiskPathRender,
        settings: blackBelowTooSlowRiskSettings,
        capabilityLineScreenPoints,
        additionalCapabilityLineScreenPointsList:
          additionalCapabilityLinePolylines,
        fadingCapabilityLinePolylines: planPreviewCarve,
        anchor: 'bottom',
        plotSpanX,
        tileShadeField: trailingActorRiskShadeField,
        shadeModel: trailingActorRiskShadeField
          ? {
              kind: 'trailingCdf',
              survivalYears: trailingActorRiskSurvival,
              stops: backgroundPatternSettings.trailingActorRiskConfidenceStops,
              yearForScreenX,
              valueForY,
            }
          : null,
      }) ?? undefined
    },
    [
      effectiveBlackBelowTooSlowRisk,
      blackBelowTooSlowRiskPathRender,
      blackBelowTooSlowRiskSettings,
      capabilityLineScreenPoints,
      additionalCapabilityLinePolylines,
      planPreviewCarve,
      plotSpanX,
      trailingActorRiskShadeField,
      trailingActorRiskSurvival,
      backgroundPatternSettings.trailingActorRiskConfidenceStops,
      yearForScreenX,
      valueForY,
    ],
  )
  const capabilityThresholdOverlay = useMemo(
    () => numCapabilityThresholdLabels > 0
      ? createCapabilityThresholdOverlay({
        graphPoints: primaryVisibleGraphPoints,
        currentTimestamp,
        maxLabels: Math.floor(numCapabilityThresholdLabels),
        xForTimestamp,
        yForValue,
        width,
        height,
        padding,
        textColor,
        backgroundColor,
        activePlan,
      })
      : { annotations: [], markers: [] },
    [
      numCapabilityThresholdLabels,
      primaryVisibleGraphPoints,
      currentTimestamp,
      xForTimestamp,
      yForValue,
      width,
      height,
      padding,
      textColor,
      backgroundColor,
      activePlan,
    ],
  )
  const lossOfControlHoverOpacitySource = useMemo(
    () => createLossOfControlHoverOpacitySource({
      showLossOfControlLines,
      backgroundPatternEnabled: backgroundPatternSettings.enabled,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      effectiveCappedTimestamp: animatedEffectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
    }),
    [
      showLossOfControlLines,
      backgroundPatternSettings.enabled,
      lossOfControlMinPoints,
      lossOfControlMaxPoints,
      animatedEffectiveCappedTimestamp,
      xForTimestamp,
      yForValue,
    ],
  )
  const hoverPoints = useMemo(
    () => createHoverPoints({
      primaryVisibleGraphPoints,
      xForTimestamp,
      yForValue,
      lossOfControlHoverOpacitySource,
      backgroundPatternSettings,
    }),
    [
      primaryVisibleGraphPoints,
      xForTimestamp,
      yForValue,
      lossOfControlHoverOpacitySource,
      backgroundPatternSettings,
    ],
  )
  // ── The ambient risk readout (riskAmbientDisplay, tabs 46–50) ──────────────
  // The current-level takeover risk at the CURRENT story date — the same
  // number tab 40's stat shows, recomputed as the story scrolls.
  const riskAmbient = useMemo(() => {
    if (!riskAmbientDisplay || !currentPoint) return undefined
    const date = new Date(currentTimestamp)
    const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1)
    const nextYearStart = Date.UTC(date.getUTCFullYear() + 1, 0, 1)
    const yearFraction =
      date.getUTCFullYear() + (currentTimestamp - yearStart) / (nextYearStart - yearStart)
    const fraction = currentLevelRiskAt(currentPoint.value, activePlan, yearFraction)
    if (fraction === null) return undefined
    return { design: riskAmbientDisplay, fraction }
  }, [riskAmbientDisplay, currentPoint, currentTimestamp, activePlan])

  // ── The 2D risk-space explorer's closure (riskSpaceExplorer, tab 20) ───────
  // The capability value at a graph-local y, by bisecting the monotonic y
  // scale in log space (no assumption about the scale's form). The risk
  // number itself comes from the authored table (currentLevelRiskAt in the
  // renderer), not from the field's stylized shading.
  const riskSpaceValueAtY = useMemo(() => {
    if (!riskSpaceExplorer) return undefined
    return (y: number): number => {
      let lo = 0.9
      let hi = 200000
      for (let i = 0; i < 48; i++) {
        const mid = Math.sqrt(lo * hi)
        // The y scale decreases as value grows (higher capability = higher
        // on screen): a midpoint DRAWN BELOW the cursor means the cursor's
        // value is larger.
        if (yForValue(mid) > y) lo = mid
        else hi = mid
      }
      return Math.sqrt(lo * hi)
    }
  }, [riskSpaceExplorer, yForValue])
  const riskBackgroundLabels = useMemo(
    () => createRiskBackgroundLabels({
      redAboveLossOfControl,
      showRiskBackgroundLabels,
      lossOfControlHoverOpacitySource,
      gradientStrength: backgroundPatternSettings.gradientStrength,
      ditherDensity: backgroundPatternSettings.ditherDensity,
      paddingLeft: padding.left,
      paddingBottom: padding.bottom,
      height,
    }),
    [
      redAboveLossOfControl,
      showRiskBackgroundLabels,
      lossOfControlHoverOpacitySource,
      backgroundPatternSettings.gradientStrength,
      backgroundPatternSettings.ditherDensity,
      padding.left,
      padding.bottom,
      height,
    ],
  )
  const futureGraphPointsByTimeline = [...futureGraphPoints].sort(
    (futureGraphPointA, futureGraphPointB) =>
      futureGraphPointA.timestamp - futureGraphPointB.timestamp ||
      futureGraphPointA.value - futureGraphPointB.value
  )
  const lastFutureProjectedPoint =
    futureGraphPointsByTimeline[futureGraphPointsByTimeline.length - 1]
  const futureValueAnnotation = createFutureValueAnnotation({
    showTimelineUnits,
    lastFutureProjectedPoint,
    xForTimestamp,
    yForValue,
    formatProjectedValue,
  })
  const sideUnitAnnotations = createSideUnitAnnotations({
    showSideUnits,
    yDomainMax,
    yForValue,
  })
  const pastLineSourceGraphPoints = renderSourceState.lineSources
    .filter(lineSource => lineSource.key === 'past' || lineSource.key === 'shared-past')
    .flatMap(lineSource => lineSource.graphPoints)
  const leftmostPastLinePoint = pastLineSourceGraphPoints.reduce<
    typeof pastLineSourceGraphPoints[number] | undefined
  >(
    (currentLeftmost, candidatePoint) =>
      !currentLeftmost || candidatePoint.timestamp < currentLeftmost.timestamp
        ? candidatePoint
        : currentLeftmost,
    undefined,
  )
  const aiCapabilitiesLabelAnnotation = showAICapabilitiesLabel
    ? createAICapabilitiesLabelAnnotation({
      leftmostPastLinePoint,
      xForTimestamp,
      yForValue,
      fontSize: backgroundPatternSettings.aiCapabilitiesLabelFontSize,
    })
    : undefined
  const lossOfControlAccentColor = getLossOfControlAccentColor()
  const inlineCapabilityThresholdAnnotations = showCapabilityThresholdLabels
    ? capabilityThresholdOverlay.annotations
    : []
  const hoverCapabilityThresholdAnnotations = !showCapabilityThresholdLabels
    ? capabilityThresholdOverlay.annotations
    : []
  const customAnnotationEntries: ExtrapolatedGraphAnnotation[] = customAnnotations
    ? customAnnotations.map((ann) => ({
        key: ann.key,
        x: ann.xPct * width,
        y: ann.yPct * height,
        text: ann.text,
        transform: ann.transform,
        textShadowColor: ann.textShadowColor,
        pointerEvents: ann.pointerEvents,
        transitionDurationMs: GRAPH_LABEL_FADE_DURATION_MS,
      }))
    : []
  const annotations = addGalaxyTextShadow([
    ...sideUnitAnnotations,
    ...(
      futureValueAnnotation
        ? [futureValueAnnotation]
        : []
    ),
    ...planPreviewOverlay.endLabelAnnotations,
    ...inlineCapabilityThresholdAnnotations,
    ...(aiCapabilitiesLabelAnnotation ? [aiCapabilitiesLabelAnnotation] : []),
    ...customAnnotationEntries,
  ], showGalaxy)
  const hoverRevealAnnotations = addGalaxyTextShadow([
    ...hoverCapabilityThresholdAnnotations,
  ], showGalaxy)
  const currentValueLabel = createCurrentValueLabel({
    showTimelineUnits,
    currentValue: renderSourceState.currentValue,
    formatCurrentValue,
  })
  const currentValueLabelPosition = createCurrentValueLabelPosition({
    currentValueLabelPoint: renderSourceState.currentMarkerSources[0]?.graphPoints[0],
    xForTimestamp,
    yForValue,
  })
  const baseBottomAxisLabels = bottomAxisYears && bottomAxisYears.length > 0
    ? bottomAxisYears.map((year) => ({
        key: `${year}`,
        x: xForTimestamp(Date.UTC(year, 0, 1)),
        text: `${year}`,
        textAnchor: 'middle' as 'start' | 'middle' | 'end',
        opacity: 0.85,
      }))
    : createBottomAxisLabels({
        showGalaxy,
        showAllYears,
        currentYear,
        leftYear,
        rightYear,
        currentTimelineX,
        padding,
        innerWidth,
        width,
        galaxySettings: resolvedScaleSettings.galaxy,
        xForTimestamp,
      })
  // Pill mode moves the year readout off the timeline into the top-left pill,
  // so the bottom axis drops ALL of its year ticks (the moving current-year
  // one and the static start/end ones).
  const pillFilteredBottomAxisLabels = currentYearPill ? [] : baseBottomAxisLabels
  const transitionTriggerYear = (targetDate ?? currentDate).getUTCFullYear()
  const bottomAxisLabels = useTransitioningAxisLabels({
    baseLabels: pillFilteredBottomAxisLabels,
    currentYear,
    transitionTriggerYear,
    xForTimestamp,
  })

  // "Golden path" wedge between the red field's bottom (LOC MIN) and
  // the black field's top (too-slow MAX), under the dither overlays.
  const safeZoneOverlay = (() => {
    if (!safeZoneColor) return null
    const topPts = lossOfControlMinPoints.filter(p => Number.isFinite(p.value))
    const botPts = tooSlowRiskMaxPoints.filter(p => Number.isFinite(p.value))
    if (topPts.length < 2 || botPts.length < 2) return null
    const top = topPts.map(p =>
      `${xForTimestamp(p.timestamp).toFixed(2)},${(yForValue(p.value) + safeZoneTopOffsetPx).toFixed(2)}`,
    )
    const bot = botPts.slice().reverse().map(p =>
      `${xForTimestamp(p.timestamp).toFixed(2)},${(yForValue(p.value) - safeZoneBottomOffsetPx).toFixed(2)}`,
    )
    const d = `M ${[...top, ...bot].join(' L ')} Z`
    const gradientId = 'safe-zone-gradient'
    const filterId = 'safe-zone-blur'
    const useGradient = !!safeZoneColorLeft && safeZoneColorLeft !== safeZoneColor
    const useBlur = safeZoneEdgeBlurPx > 0
    // Gradient maps across the polygon's x extent, not the whole chart.
    const xs = [...topPts, ...botPts].map(p => xForTimestamp(p.timestamp))
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    return (
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      >
        {(useGradient || useBlur) && (
          <defs>
            {useGradient && (() => {
              // Symmetric stops so the 50/50 mix lands at the midpoint.
              const mid = Math.max(0, Math.min(1, safeZoneGradientMidpoint))
              const p1 = Math.max(0, 2 * mid - 1)
              const p2 = Math.min(1, 2 * mid)
              return (
                <linearGradient
                  id={gradientId}
                  gradientUnits="userSpaceOnUse"
                  x1={minX} x2={maxX} y1={0} y2={0}
                >
                  <stop offset={`${p1 * 100}%`} stopColor={safeZoneColorLeft} />
                  <stop offset={`${p2 * 100}%`} stopColor={safeZoneColor} />
                </linearGradient>
              )
            })()}
            {useBlur && (
              // Extra padding so blur isn't clipped by the filter box's 10% inset.
              <filter id={filterId} x="-20%" y="-50%" width="140%" height="200%">
                <feGaussianBlur stdDeviation={safeZoneEdgeBlurPx} />
              </filter>
            )}
          </defs>
        )}
        <path
          d={d}
          fill={useGradient ? `url(#${gradientId})` : safeZoneColor}
          opacity={safeZoneOpacity}
          filter={useBlur ? `url(#${filterId})` : undefined}
        />
      </svg>
    )
  })()

  return {
    width,
    height,
    textColor,
    backgroundColor,
    title,
    graphLabel,
    currentValueLabel,
    currentValueLabelPosition,
    redAboveLossOfControlCanvasLayer,
    blackBelowTooSlowRiskCanvasLayer,
    clipLinesToDitherTop,
    lossOfControlGradientStrip: (
      <ExtrapolatedGraphLossOfControlLayer
        showLossOfControlLines={showLossOfControlLines}
        redAboveLossOfControl={redAboveLossOfControl}
        redAboveLossOfControlCanvasLayerRendered={
          redAboveLossOfControlCanvasLayer !== undefined
        }
        redAboveLossOfControlFaded={redAboveLossOfControlFaded}
        activePlan={activePlan}
        capabilityLinePoints={primaryVisibleGraphPoints}
        capabilityLineSmoothing={backgroundPatternSettings.capabilityLineSmoothing}
        additionalCapabilityLinePolylines={additionalCapabilityLinePolylines}
        fadingCapabilityLinePolylines={planPreviewCarve}
        lossOfControlMinPoints={lossOfControlMinPoints}
        lossOfControlMaxPoints={lossOfControlMaxPoints}
        effectiveCappedTimestamp={animatedEffectiveCappedTimestamp}
        xForTimestamp={xForTimestamp}
        yForValue={yForValue}
        valueForY={valueForY}
        showTopDashedLine={showTopDashedLine}
        lossOfControlAccentColor={lossOfControlAccentColor}
        riskBackgroundLabels={riskBackgroundLabels}
        plotSpanX={plotSpanX}
      />
    ),
    editorDotsOverlay: lossOfControlEditorMode ? (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          pointerEvents: 'none',
        }}
      >
        <LossOfControlEditorDots
          activePlan={activePlan}
          effectiveCappedTimestamp={animatedEffectiveCappedTimestamp}
          xForTimestamp={xForTimestamp}
          yForValue={yForValue}
        />
      </svg>
    ) : null,
    safeZoneOverlay,
    lineSegments: renderedLineSegments,
    futurePointMarkers: showYearDots ? futureMarkers : [],
    pastPointMarkers: showYearDots ? visiblePastMarkers : [],
    currentPointMarkers: (showYearDots || showCurrentPointMarker) ? currentMarkers : [],
    annotationMarkers: capabilityThresholdOverlay.markers,
    activePlan,
    bottomAxisLabels,
    annotations,
    hoverRevealAnnotations,
    hoverPoints,
    onHoveredYearChange,
    pointTooltipDetailsHint,
    pointHoverMaxDistanceToLinePx,
    takeoverRiskAreaHover,
    riskFoomPreview,
    riskFoomDesign,
    riskFoomTedCaption,
    riskFoomOnPointHover,
    riskSpaceValueAtY,
    riskSpaceTimelineGuide:
      riskSpaceExplorer === "timeline-guide"
      || riskSpaceExplorer === "timeline-guide-above-globe",
    riskSpaceAboveGlobe: riskSpaceExplorer === "timeline-guide-above-globe",
    riskTedGuideYForValue: riskTedGuide ? yForValue : undefined,
    riskAmbient,
    riskHoverDimDisabled,
    riskTedGuideBelowGlobe:
      riskTedGuide === "below-earth" || riskTedGuide === "below-earth-subtle",
    riskTedGuideSubtleLine: riskTedGuide === "below-earth-subtle",
    highlightLineWhilePointHovered,
    riskZoneDimTween,
    riskZoneDimInverted,
    riskZoneDimDotShrink,
    riskZoneContinuousGuide,
    pointGuideTedDash,
    lossOfControlHoverReveal,
    showAllYearDotTooltips: showAllYears,
    currentYearPillYear: currentYearPill ? currentYear : undefined,
    // Quantize the pill's month to a 4-month grid (Jan / May / Sep) so the
    // branch-plan date reads as coarse 4-month steps. The date it derives from
    // is spring-animated (useAnimatedDate), so it glides through every month
    // between the scroll snap points; flooring here keeps the displayed month
    // on the same 4-month grid the scroll date snaps to (snapTimeToThirdOfYear)
    // instead of ticking month-by-month.
    currentYearPillMonthIndex:
      currentYearPill && currentYearPillShowMonth
        ? Math.floor(currentMonthIndex / 4) * 4
        : undefined,
    currentYearPillFlushLeft,
    currentYearPillAnchorY,
    onCurrentYearPillClick,
    currentYearPillMenuOpen,
    showCurrentPointCapabilityLabel,
  }
}
